import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  CheckCircle2,
  History,
  MoreHorizontal,
  Play,
  Plus,
  Power,
  Trash2,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { AlertIncidentDisposition, type DispositionAction } from '@/components/alerts/AlertIncidentDisposition';
import {
  ExecuteCooldownError,
  type AlertDefinition,
  useAlertDefinitions,
  useDeleteAlertDefinition,
  useDisableAlertDefinition,
  useEnableAlertDefinition,
  useExecuteAlertDefinition,
} from '@/hooks/useAlertDefinitions';
import { AlertFormModal } from '@/components/alerts/AlertFormModal';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Alerts — list of alert rules + scheduled tasks. Migrated from Bulma
 * table to ResponsiveTable + PageHeader. Spec §4 MF (oncall-critical).
 *
 * Significant UX changes from the Bulma original:
 *   • Inline "History" expansion is dropped. Clicking the row name
 *     navigates to AlertDetails (/alerts/:id) which has execution
 *     history + overrides + open incidents.
 *   • Row actions (Enable/Disable, Run, Delete) collapse into a single
 *     kebab DropdownMenu instead of inline button cluster.
 *
 * Behavior preserved:
 *   • Deep-link entry /alerts?edit=:id opens the edit modal once the
 *     list is loaded, then strips the param so back/forward doesn't
 *     re-trigger.
 *   • Run-now cooldown surfaces as a warning notice.
 *   • Delete confirm prompt.
 *   • system_default rows can be disabled but not deleted.
 */

const TASK_TYPE_LABEL: Record<string, string> = {
  metric_alert: 'Metric alert',
  device_audit: 'Device audit',
  custom_analysis: 'Analysis',
};

function formatInterval(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m`;
  if (seconds < 86400) return `${String(Math.floor(seconds / 3600))}h`;
  return `${String(Math.floor(seconds / 86400))}d`;
}

interface RunNotice {
  kind: 'success' | 'warning' | 'error';
  text: string;
}

export default function Alerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const organizationId = user?.organizationId ?? undefined;

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [enabledFilter, setEnabledFilter] = useState<string>('all');
  const [isModalActive, setIsModalActive] = useState(false);
  const [editingTask, setEditingTask] = useState<AlertDefinition | null>(null);
  /// The pause/delete decision in flight, if any. Holding the task rather than an id keeps
  /// the dialog's title stable while the mutation runs and the list refetches underneath it.
  const [disposition, setDisposition] = useState<{ action: DispositionAction; task: AlertDefinition } | null>(null);
  const [runNotice, setRunNotice] = useState<RunNotice | null>(null);

  const { data: tasks, isLoading } = useAlertDefinitions(organizationId, {
    taskType: typeFilter === 'all' ? undefined : typeFilter,
    enabled: enabledFilter === 'all' ? undefined : enabledFilter === 'true',
  });

  // Deep-link entry /alerts?edit=:id — open the edit modal once tasks
  // are loaded, then strip the param so back/forward doesn't repeat it.
  const [searchParams, setSearchParams] = useSearchParams();
  const editTaskIdFromUrl = searchParams.get('edit');
  const deepLinkedTask = editTaskIdFromUrl ? tasks?.find((t) => t.id === editTaskIdFromUrl) : undefined;
  const [openedDeepLink, setOpenedDeepLink] = useState<AlertDefinition | undefined>(undefined);
  if (deepLinkedTask && deepLinkedTask !== openedDeepLink) {
    setOpenedDeepLink(deepLinkedTask);
    setEditingTask(deepLinkedTask);
    setIsModalActive(true);
  }
  // Stripping the param is a navigation, so it stays an effect.
  useEffect(() => {
    if (!deepLinkedTask) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      },
      { replace: true },
    );
  }, [deepLinkedTask, setSearchParams]);

  const enableMutation = useEnableAlertDefinition();
  const disableMutation = useDisableAlertDefinition();
  const executeMutation = useExecuteAlertDefinition();
  const deleteMutation = useDeleteAlertDefinition();

  const handleRun = async (taskId: string) => {
    setRunNotice(null);
    try {
      await executeMutation.mutateAsync(taskId);
      setRunNotice({ kind: 'success', text: 'Alert queued for immediate re-evaluation.' });
    } catch (err) {
      if (err instanceof ExecuteCooldownError) {
        setRunNotice({ kind: 'warning', text: err.message });
      } else {
        setRunNotice({ kind: 'error', text: 'Failed to re-run alert.' });
      }
    }
  };

  const columns = useMemo<readonly ResponsiveTableColumn<AlertDefinition>[]>(
    () => [
      {
        key: 'name',
        label: 'Name',
        hideMobileLabel: true,
        render: (task) => (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Link
                to={`/alerts/${task.id}`}
                className="font-medium hover:underline truncate"
              >
                {task.name}
              </Link>
              {task.source === 'system_default' ? (
                <Badge variant="outline" className="text-[10px]">
                  Default
                </Badge>
              ) : null}
            </div>
            {task.description ? (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {task.description}
              </p>
            ) : null}
            {task.lastError ? (
              <p className="text-xs text-destructive mt-0.5 line-clamp-1">
                Error: {task.lastError}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        render: (task) => (
          <Badge variant="secondary" className="font-normal">
            {TASK_TYPE_LABEL[task.taskType] ?? task.taskType}
          </Badge>
        ),
      },
      {
        key: 'interval',
        label: 'Interval',
        hideAt: 'mobile',
        render: (task) => (
          <span className="text-sm font-mono text-muted-foreground">
            {formatInterval(task.intervalSeconds)}
          </span>
        ),
      },
      {
        key: 'state',
        label: 'State',
        render: (task) => <AlertStateBadge state={task.alertState} />,
      },
      {
        key: 'lastResult',
        label: 'Last result',
        hideAt: 'mobile',
        render: (task) =>
          task.lastResult ? <LastResultBadge result={task.lastResult} /> : null,
      },
      {
        key: 'lastRun',
        label: 'Last run',
        hideAt: 'mobile',
        render: (task) => (
          <span className="text-xs text-muted-foreground">
            {task.lastRunAt
              ? formatDistanceToNow(new Date(task.lastRunAt), { addSuffix: true })
              : 'Never'}
          </span>
        ),
      },
      {
        key: 'enabled',
        label: 'Enabled',
        render: (task) =>
          task.enabled ? (
            <Badge variant="secondary" className="bg-sev-4/15 text-sev-4 border-0">
              On
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              Off
            </Badge>
          ),
      },
    ],
    [],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Alert rules"
        subtitle="Schedule, run, and silence alert evaluations across your network"
        primary={
          <Button
            onClick={() => {
              setEditingTask(null);
              setIsModalActive(true);
            }}
          >
            <Plus className="w-4 h-4" />
            New alert rule
          </Button>
        }
      />

      {runNotice ? (
        <Alert
          variant={runNotice.kind === 'error' ? 'destructive' : 'default'}
          className={cn(
            'mb-4 relative',
            runNotice.kind === 'warning' && 'border-sev-3 bg-sev-3-soft text-foreground',
            runNotice.kind === 'success' && 'border-sev-4 bg-sev-4/10 text-foreground',
          )}
        >
          {runNotice.kind === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{runNotice.text}</AlertDescription>
          <button
            type="button"
            onClick={() => { setRunNotice(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
        <div className="space-y-1.5">
          <Label htmlFor="alerts-type-filter" className="text-xs">Type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger id="alerts-type-filter" className="w-full md:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="metric_alert">Metric alert</SelectItem>
              <SelectItem value="device_audit">Device audit</SelectItem>
              <SelectItem value="custom_analysis">Custom analysis</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="alerts-enabled-filter" className="text-xs">Status</Label>
          <Select value={enabledFilter} onValueChange={setEnabledFilter}>
            <SelectTrigger id="alerts-enabled-filter" className="w-full md:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Enabled</SelectItem>
              <SelectItem value="false">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <ResponsiveTable<AlertDefinition>
        columns={columns}
        rows={tasks ?? []}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/alerts/${r.id}`); }}
        rowActions={(task) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${task.name}`}
                onClick={(e) => { e.stopPropagation(); }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {task.enabled ? (
                <DropdownMenuItem
                  onClick={() => { setDisposition({ action: 'pause', task }); }}
                  disabled={disableMutation.isPending}
                  className="gap-2"
                >
                  <Power className="w-4 h-4" />
                  Pause
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => { enableMutation.mutate(task.id); }}
                  disabled={enableMutation.isPending}
                  className="gap-2"
                >
                  <Power className="w-4 h-4" />
                  Enable
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => { void handleRun(task.id); }}
                disabled={executeMutation.isPending}
                className="gap-2"
              >
                <Play className="w-4 h-4" />
                Run now
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/alerts/${task.id}`} className="gap-2 cursor-pointer">
                  <History className="w-4 h-4" />
                  View history
                </Link>
              </DropdownMenuItem>
              {task.source !== 'system_default' ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => { setDisposition({ action: 'delete', task }); }}
                    disabled={deleteMutation.isPending}
                    className="gap-2 text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            title="No alert rules configured"
            description="Alert rules and scheduled device audits will appear here once you create one."
            action={
              <Button
                onClick={() => {
                  setEditingTask(null);
                  setIsModalActive(true);
                }}
              >
                <Plus className="w-4 h-4" />
                New alert rule
              </Button>
            }
          />
        }
      />

      <AlertFormModal
        isOpen={isModalActive}
        task={editingTask}
        onClose={() => {
          setIsModalActive(false);
          setEditingTask(null);
        }}
      />
      <AlertIncidentDisposition
        open={disposition !== null}
        action={disposition?.action ?? 'pause'}
        taskId={disposition?.task.id ?? null}
        taskName={disposition?.task.name ?? ''}
        busy={deleteMutation.isPending || disableMutation.isPending}
        onCancel={() => { setDisposition(null); }}
        onConfirm={(resolveIncidents) => {
          if (!disposition) return;
          const { action, task } = disposition;
          const mutation = action === 'delete' ? deleteMutation : disableMutation;
          mutation.mutate(
            { taskId: task.id, resolveIncidents },
            { onSettled: () => { setDisposition(null); } },
          );
        }}
      />
    </PageContainer>
  );
}

// ─── Small badge helpers ──────────────────────────────────────────────

function AlertStateBadge({ state }: { state: 'ok' | 'alerting' | 'pending' }) {
  if (state === 'alerting') {
    return (
      <Badge className="bg-sev-1/15 text-sev-1 border-0 hover:bg-sev-1/15">alerting</Badge>
    );
  }
  if (state === 'pending') {
    return (
      <Badge className="bg-sev-3/20 text-foreground border-0 hover:bg-sev-3/20">pending</Badge>
    );
  }
  return (
    <Badge className="bg-sev-4/15 text-sev-4 border-0 hover:bg-sev-4/15">ok</Badge>
  );
}

function LastResultBadge({ result }: { result: string }) {
  if (result === 'alert') {
    return <Badge className="bg-sev-1/15 text-sev-1 border-0">alert</Badge>;
  }
  if (result === 'error') {
    return <Badge className="bg-sev-3/20 text-foreground border-0">error</Badge>;
  }
  if (result === 'ok') {
    return <Badge className="bg-sev-4/15 text-sev-4 border-0">ok</Badge>;
  }
  return <Badge variant="outline">{result}</Badge>;
}
